import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { syncPayrollReadiness } from "./payrollReadinessSync";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  EmiratisationHeadcountBandEnum,
  GratuityBasisSalaryEnum,
  PaymentMethodTypeEnum,
} from "@/utils/enums/payroll.enum";

export type ICountry = {
  id: number;
  name: string;
  countryCode: string;
  nationality: string;
};

export type ICurrency = {
  id: number;
  code: string;
  name: string;
  symbol: string;
};

export type ICountryRule = {
  /**
   * The RULE's id. Every SIF-bank endpoint below is nested under this — the
   * route is `country-rules/:countryRuleId` — which is why their param is
   * named `countryRuleId` and not `countryId`.
   */
  id: number;
  companyId: number;
  /** The actual country this rule is for. NOT interchangeable with `id`. */
  countryId: number;
  currencyId: number;
  isActive: boolean;
  country: ICountry;
  currency: ICurrency;
  statutorySettings: IStatutoryConfig | null;
  emiratisationSettings: IEmiratisationConfig | null;
  gratuitySettings: IGratuityConfig | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type WpsExportProfile = {
  delimiter?: ',' | '|' | ';';
  quoteFields?: boolean;
  lineEnding?: 'CRLF' | 'LF';
  /** `M/D/YYYY` is unpadded (`6/1/2026`) — what real UAE agent-bank files carry. */
  dateFormat?: 'YYYY-MM-DD' | 'YYYYMMDD' | 'DD/MM/YYYY' | 'M/D/YYYY';
  /** Decimals on every amount. 2 gives `125000.00`, 0 gives `125000`. */
  amountDecimals?: 0 | 2;
  /** SCR salary-month field. `MYYYY` gives `62026`, `MMYYYY` gives `062026`. */
  salaryMonthFormat?: 'MMYYYY' | 'MYYYY';
  emptyFieldValue?: '0' | '';
  scrPosition?: 'FIRST' | 'LAST';
  encoding?: 'UTF-8' | 'ANSI';
  /**
   * File name template. Tokens: `{companyName}`, `{establishmentId}`,
   * `{employerBankCode}`, `{yyyymm}`, `{yyyymmdd}`. Omit for the default,
   * `WPS-AE-{companyName}-{yyyymm}.SIF`. The server sanitises the substituted
   * result, so an odd company name cannot produce an odd path.
   */
  fileNamePattern?: string;
};

export type ISifBank = {
  id: number;
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  wpsAgentId?: string;
  bankRoutingCode?: string;
  swiftCode?: string;
  currency?: string;
  isDefault?: boolean;
  isActive?: boolean;
  wpsProfile?: WpsExportProfile;
};

export type IStatutoryConfig = {
  gpssa: {
    enabled: boolean;
    employeeContributionRate: number;
    employerContributionRate: number;
    pensionableSalaryMin: number;
    pensionableSalaryCap: number;
    governmentContribution: number;
  };
  minimumWage: {
    enforce: boolean;
    pensionableSalaryMin: number;
  };
};

export type IEmiratisationYearlyTarget = {
  year: number;
  targetPercentage: number;
};

export type IEmiratisationConfig = {
  enabled: boolean;
  headcountBand: EmiratisationHeadcountBandEnum;
  penaltyPerMissingHireAed: number;
  yearlyTargets: IEmiratisationYearlyTarget[];
  gracePeriodDays: number;
};

export type IGratuityConfig = {
  enabled: boolean;
  basisSalary: GratuityBasisSalaryEnum;
  capMonths: number;
  years1To5Rate: number;
  years5PlusRate: number;
  resignationReduction: boolean;
};

export type IWpsConfig = {
  enabled: boolean;
  registeredBankId: number;
  establishmentId: string;
  employerId: string;
  paymentDeadlineDay: number;
  alertBeforeDeadlineDays: number;
  freezeBeforeSifExport: boolean;
};

export type IPaymentMethodConfig = {
  defaultMethod: PaymentMethodTypeEnum;
  allowCashPayments: boolean;
  cashPayerUserId: number | null;
  requireEmployeeSignature: boolean;
  /** B27: who prepared a run or settlement may not approve it. Absent = on. */
  requireSecondApprover?: boolean;
};

export type IDeactivationImpact = {
  activeEmployees: number;
  activePayrollRuns: number;
  linkedBranches: number;
};

export type IGetCountryRulesParams = {
  page?: number;
  limit?: number;
  /** Free-text match on country name — applied server-side. */
  search?: string;
  isActive?: boolean;
};

export type IGetSifBanksParams = {
  countryRuleId: number;
  page?: number;
  limit?: number;
  /** Matches bank name / account holder / account number, server-side. */
  search?: string;
};

/** Sample SIF built from a bank's export profile — never a real payroll file. */
export type ISifBankPreview = {
  fileName: string;
  content: string;
  profile: WpsExportProfile | null;
  isSample: boolean;
  note: string;
};

export type ISifBankPayload = {
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  wpsAgentId?: string;
  bankRoutingCode?: string;
  swiftCode?: string;
  currency?: string;
  isDefault?: boolean;
  isActive?: boolean;
  wpsProfile?: WpsExportProfile;
};

export const companyCountryRulesApi = createApi({
  reducerPath: "companyCountryRulesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "countryRules",
    "countryRule",
    "sifBanks",
    "sifBank",
    "countryRule:statutory",
    "countryRule:emiratisation",
    "countryRule:gratuity",
  ],
  endpoints: (builder) => ({
    getCountryRules: builder.query<IApiPaginatedResponse<ICountryRule[]>, IGetCountryRulesParams | void>({
      query: (params) => ({
        url: "/company/settings/payroll/country-rules",
        method: "GET",
        params: {
          page:  params?.page ?? 1,
          limit: params?.limit ?? 10,
          // Filtering is the server's job: the list is paginated, so filtering a
          // received page would search only the rows that happen to be on it.
          ...(params?.search ? { search: params.search } : {}),
          ...(typeof params?.isActive === "boolean" ? { isActive: params.isActive } : {}),
        },
      }),
      providesTags: ["countryRules"],
    }),
    getDeactivationImpact: builder.query<IApiResponse<IDeactivationImpact>, number>({
      query: (id) => ({
        url: `/company/settings/payroll/country-rules/${id}/deactivation-impact`,
        method: "GET",
      }),
    }),
    /**
     * Server-side TOGGLE — it flips `isActive` itself and ignores any body, so
     * sending the desired value would be misleading (and a double submit would
     * flip twice). Callers must guard against a second click while in flight.
     */
    toggleCountryRuleStatus: builder.mutation<IApiResponse<ICountryRule>, number>({
      query: (id) => ({
        url: `/company/settings/payroll/country-rules/${id}/status`,
        method: "PATCH",
      }),
      invalidatesTags: ["countryRules"],
      // Readiness reports a deactivated default rule / registered SIF bank (R22).
      onQueryStarted: syncPayrollReadiness,
    }),
    getSifBanks: builder.query<IApiPaginatedResponse<ISifBank[]>, IGetSifBanksParams>({
      query: ({ countryRuleId, page = 1, limit = 10, search }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/sif-banks`,
        method: "GET",
        params: { page, limit, ...(search ? { search } : {}) },
      }),
      providesTags: ["sifBanks"],
    }),
    /**
     * A sample salary file built from this bank's export profile.
     *
     * Lazy: it is fetched when the user asks to preview, not on mount — the
     * content is only ever wanted on demand, and it is a file body rather than
     * screen state.
     */
    getSifBankPreview: builder.query<
      IApiResponse<ISifBankPreview>,
      { countryRuleId: number; id: number }
    >({
      query: ({ countryRuleId, id }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/sif-banks/${id}/preview`,
        method: "GET",
      }),
    }),

    getSifBankById: builder.query<IApiResponse<ISifBank>, { countryRuleId: number; id: number }>({
      query: ({ countryRuleId, id }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/sif-banks/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, { id }) => [{ type: "sifBank", id }],
    }),
    createSifBank: builder.mutation<IApiResponse<ISifBank>, { countryRuleId: number; data: ISifBankPayload }>({
      query: ({ countryRuleId, data }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/sif-banks`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["sifBanks"],
      // Readiness reports a deactivated default rule / registered SIF bank (R22).
      onQueryStarted: syncPayrollReadiness,
    }),
    updateSifBank: builder.mutation<IApiResponse<ISifBank>, { countryRuleId: number; id: number; data: ISifBankPayload }>({
      query: ({ countryRuleId, id, data }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/sif-banks/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => ["sifBanks", { type: "sifBank", id }],
      // Readiness reports a deactivated default rule / registered SIF bank (R22).
      onQueryStarted: syncPayrollReadiness,
    }),
    /**
     * Server-side toggle, same contract as the country-rule one: no body, and
     * the caller must not fire it twice concurrently.
     */
    toggleSifBankStatus: builder.mutation<IApiResponse<ISifBank>, { countryRuleId: number; id: number }>({
      query: ({ countryRuleId, id }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/sif-banks/${id}/status`,
        method: "PATCH",
      }),
      invalidatesTags: ["sifBanks"],
      // Readiness reports a deactivated default rule / registered SIF bank (R22).
      onQueryStarted: syncPayrollReadiness,
    }),
    deleteSifBank: builder.mutation<IApiResponse<null>, { countryRuleId: number; id: number }>({
      query: ({ countryRuleId, id }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/sif-banks/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["sifBanks"],
      // Readiness reports a deactivated default rule / registered SIF bank (R22).
      onQueryStarted: syncPayrollReadiness,
    }),
    getStatutory: builder.query<IApiResponse<IStatutoryConfig | null>, number>({
      query: (countryRuleId) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/statutory`,
        method: "GET",
      }),
      transformResponse: (
        raw: IApiResponse<{ statutorySettings: IStatutoryConfig | Record<string, never> | null } | null>,
      ) => {
        const settings = raw.data?.statutorySettings;
        const hydrated =
          settings && typeof settings === "object" && "gpssa" in settings
            ? (settings as IStatutoryConfig)
            : null;
        return { ...raw, data: hydrated };
      },
      providesTags: (_r, _e, id) => [{ type: "countryRule:statutory", id }],
    }),
    updateStatutory: builder.mutation<IApiResponse<IStatutoryConfig>, { countryRuleId: number; data: IStatutoryConfig }>({
      query: ({ countryRuleId, data }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/statutory`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_r, _e, { countryRuleId }) => [{ type: "countryRule:statutory", id: countryRuleId }],
    }),
    getEmiratisation: builder.query<IApiResponse<IEmiratisationConfig | null>, number>({
      query: (countryRuleId) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/emiratisation`,
        method: "GET",
      }),
      transformResponse: (
        raw: IApiResponse<
          | IEmiratisationConfig
          | { emiratisationSettings: IEmiratisationConfig | Record<string, never> | null }
          | null
        >,
      ) => {
        const maybeSettings =
          raw.data && "emiratisationSettings" in raw.data
            ? raw.data.emiratisationSettings
            : raw.data;
        const hydrated =
          maybeSettings && typeof maybeSettings === "object" && "headcountBand" in maybeSettings
            ? (maybeSettings as IEmiratisationConfig)
            : null;
        return { ...raw, data: hydrated };
      },
      providesTags: (_r, _e, id) => [{ type: "countryRule:emiratisation", id }],
    }),
    updateEmiratisation: builder.mutation<IApiResponse<IEmiratisationConfig>, { countryRuleId: number; data: IEmiratisationConfig }>({
      query: ({ countryRuleId, data }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/emiratisation`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_r, _e, { countryRuleId }) => [{ type: "countryRule:emiratisation", id: countryRuleId }],
    }),
    getGratuity: builder.query<
      IApiResponse<IGratuityConfig | null> & { countryCode?: string | null },
      number
    >({
      query: (countryRuleId) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/gratuity`,
        method: "GET",
      }),
      transformResponse: (
        raw: IApiResponse<
          | {
              gratuitySettings: IGratuityConfig | Record<string, never> | null;
              country?: { countryCode?: string | null } | null;
            }
          | IGratuityConfig
          | null
        >,
      ) => {
        // The rule's country decides the legal minimum the form checks (B14).
        const countryCode =
          raw.data && "country" in raw.data
            ? (raw.data.country?.countryCode ?? null)
            : null;
        const maybeSettings =
          raw.data && "gratuitySettings" in raw.data
            ? raw.data.gratuitySettings
            : raw.data;
        const hydrated =
          maybeSettings && typeof maybeSettings === "object" && "basisSalary" in maybeSettings
            ? (maybeSettings as IGratuityConfig)
            : null;
        return { ...raw, data: hydrated, countryCode };
      },
      providesTags: (_r, _e, id) => [{ type: "countryRule:gratuity", id }],
    }),
    updateGratuity: builder.mutation<IApiResponse<IGratuityConfig>, { countryRuleId: number; data: IGratuityConfig }>({
      query: ({ countryRuleId, data }) => ({
        url: `/company/settings/payroll/country-rules/${countryRuleId}/gratuity`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_r, _e, { countryRuleId }) => [{ type: "countryRule:gratuity", id: countryRuleId }],
    }),
  }),
});

export const {
  useGetCountryRulesQuery,
  useLazyGetDeactivationImpactQuery,
  useToggleCountryRuleStatusMutation,
  useGetSifBanksQuery,
  useGetSifBankByIdQuery,
  useLazyGetSifBankPreviewQuery,
  useCreateSifBankMutation,
  useUpdateSifBankMutation,
  useToggleSifBankStatusMutation,
  useDeleteSifBankMutation,
  useGetStatutoryQuery,
  useUpdateStatutoryMutation,
  useGetEmiratisationQuery,
  useUpdateEmiratisationMutation,
  useGetGratuityQuery,
  useUpdateGratuityMutation,
} = companyCountryRulesApi;
